import {
    Box,
    Li,
    ModalDialog,
    ModalDialogBody,
    ModalDialogCloseButton,
    ModalDialogContent,
    ModalDialogHeader,
    ModalDialogNav,
    ModalDialogTitle,
    Typography,
    Ul,
} from '@vp/swan';
import { format, parseISO } from 'date-fns';
import { Currency, bugtracker, price } from '@99designs/design-services-common';
import { __ } from '@99designs/i18n';
import { formatDay, getSOWContent } from './sowContent';
import type { SOWVariant } from './sowContent';

function formatDate(date?: string) {
    if (!date) {
        return '';
    }

    const parsedDate = parseISO(date);
    return format(parsedDate, 'MMMM d, yyyy');
}

// This is a static equivalent of https://gitlab.com/vistaprint-org/99designs/frontend/-/blob/master/graph/graph/job.go#L58-65
export interface Agreement {
    revisions: number;
    turnaround: number;
    deliverables: number;
    deliverableType: string;
    payDeadline: number;
    requestDeadline: number;
}

// Warning: This statement of work is the read-only equivalent of the one initially shown to freelance designers when applying for a job in Matched Projects.
// Any changes here also need to be reflected in that statement of work.
//
// https://gitlab.com/vistaprint-org/99designs/frontend/-/blob/master/libs/feature-browse-projects/src/lib/JobApply/Section/JobApplication/Steps/StatementOfWork.tsx
//
// These disparate statements of work components should be extracted into a central source at some point in the near future.
// The agreement criteria templated in the statement of work should also be hoisted into the product model and set in Contentful or the PIM.
// Finally, all redundant static types and props, such as Agreement and AgreementType should be deleted.
export const SOWModal = ({
    isOpen,
    setIsOpen,
    requestId,
    agreement,
    matchedDate,
    earnings,
    isLogoTouchUp = false,
}: {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    requestId: string;
    agreement: Agreement;
    matchedDate?: string;
    earnings?: number;
    isLogoTouchUp?: boolean;
}) => {
    if (!matchedDate) {
        bugtracker.notify(
            new Error(`No matched date found shown in designer SOW for job: ${requestId}`)
        );
    }

    if (!earnings) {
        bugtracker.notify(new Error(`No earnings shown in designer SOW for job: ${requestId}`));
    }

    const variant: SOWVariant = isLogoTouchUp ? 'logoTouchUp' : 'standard';
    const formattedEarnings = price({
        amount: earnings ?? 0,
        currency: Currency.USD,
        decimalPlaces: 2,
    });
    const content = getSOWContent(variant, agreement, {
        requestId,
        formattedEarnings,
        matchedDate: formatDate(matchedDate),
    });

    return (
        <ModalDialog
            variant="panel-right"
            bodyWidth="capped"
            isOpen={isOpen}
            onRequestDismiss={() => {
                setIsOpen(false);
            }}
        >
            <ModalDialogContent aria-labelledby="modal dialog panel">
                <ModalDialogNav>
                    <ModalDialogCloseButton accessibleText="Close" />
                </ModalDialogNav>
                <ModalDialogHeader>
                    <ModalDialogTitle>{__('Statement of Work')}</ModalDialogTitle>
                </ModalDialogHeader>
                <ModalDialogBody>
                    {content.introLine && <Typography mt={6}>{content.introLine}</Typography>}
                    <Typography mt={6}>{content.firstParagraph}</Typography>

                    <Typography mt={5}>{content.projectDescription}</Typography>

                    <Typography mt={6} fontSkin={'body-standard-bold'}>
                        {__('Design Services')}
                    </Typography>
                    <Typography mt={5}>{content.designServicesIntro}</Typography>
                    <Ul mt={5}>
                        {content.inclusionBullets.map((bullet, i) => (
                            <Li key={i}>{bullet}</Li>
                        ))}
                    </Ul>

                    <Typography mt={6} fontSkin={'body-standard-bold'}>
                        {__('Deliverables')}
                    </Typography>
                    <Typography mt={5}>
                        {__(
                            'You agree to deliver the following items as part of the Design Services:'
                        )}
                    </Typography>
                    <Ul mt={5}>
                        {content.deliverableBullets.map((bullet, i) => (
                            <Li key={i}>{bullet}</Li>
                        ))}
                        {content.artboardSpecs && (
                            <Box ml={4}>
                                <Typography as="ul">
                                    {content.artboardSpecs.map((spec, index) => (
                                        <Typography as="li" key={index}>
                                            {spec}
                                        </Typography>
                                    ))}
                                </Typography>
                            </Box>
                        )}
                        {content.qualityRequirement && <Li>{content.qualityRequirement}</Li>}
                    </Ul>

                    <Typography mt={6} fontSkin={'body-standard-bold'}>
                        {__('Timeline and turnaround times')}
                    </Typography>
                    <Ul mt={5}>
                        <Li>{content.turnaroundText}</Li>
                        <Li>{content.closeText}</Li>
                    </Ul>

                    <Typography mt={6} fontSkin={'body-standard-bold'}>
                        {__('Earnings and Payment')}
                    </Typography>
                    <Ul mt={5}>
                        <Li>{content.earningsText}</Li>
                        <Li>
                            {__('Payment will be issued upon the earliest of the following:')}
                            <Ul>
                                <Li>{content.approvalText}</Li>
                                <Li>
                                    {__(
                                        '{{dayTotal}} {{dayCopy}} after all revisions have been completed OR',
                                        {
                                            ...formatDay(agreement.payDeadline),
                                            _context:
                                                'Displayed to freelance designers in the design service statement of work that informs them of the conditions for them to be paid',
                                        }
                                    )}
                                </Li>
                                <Li>
                                    {content.deliveryText}
                                    <Ul>
                                        <Li>{content.activityText}</Li>
                                    </Ul>
                                </Li>
                            </Ul>
                        </Li>
                    </Ul>

                    <Typography mt={6} fontSkin={'body-standard-bold'}>
                        {__('Additional Terms')}
                    </Typography>
                    <Typography mt={5} mb={8}>
                        {__(
                            'You are prohibited from communicating or otherwise dealing with the VistaPrint customer outside the VistaPrint Site.'
                        )}
                    </Typography>
                    {content.agreementText && (
                        <>
                            <Typography mt={6} fontSkin={'body-standard-bold'}>
                                {__('Agreement')}
                            </Typography>
                            <Typography mt={5} mb={8}>
                                {content.agreementText}
                            </Typography>
                        </>
                    )}
                </ModalDialogBody>
            </ModalDialogContent>
        </ModalDialog>
    );
};
